import type { MeSnapshot } from '../api/userland'

export type ProfileLoginKind = 'email' | 'ton' | 'telegram' | 'google' | 'other'

export type ProfileLoginIdentity = {
  kind: ProfileLoginKind
  label: string
  copyValue: string
}

export type LinkedLoginMethod = {
  kind: ProfileLoginKind
  label: string
}

/** Telegram/Google/TON session is the account. Seed reveal uses that, not a device. */
export function sessionLoginIsAccountAuth(kind: ProfileLoginKind | null | undefined): boolean {
  return kind === 'telegram' || kind === 'google' || kind === 'ton'
}

function looksLikeTonAddress(value: string): boolean {
  return /^(EQ|UQ)[A-Za-z0-9_-]{20,}$/.test(value)
}

function stripPrefix(uid: string, prefix: string): string {
  return uid.startsWith(prefix) ? uid.slice(prefix.length) : uid
}

export function profileAccountName(me: MeSnapshot | null | undefined): string {
  if (!me) return ''
  const given = (me.given_name || '').trim()
  if (given) return given
  const provider = (me.provider || '').toLowerCase()
  if (provider === 'google') {
    const n = me.display_name.trim()
    if (n && !n.includes('@')) return n
  }
  return 'Account'
}

export function profileLoginIdentity(me: MeSnapshot): ProfileLoginIdentity {
  const provider = (me.provider || '').toLowerCase()
  if (provider === 'email' || me.uid.startsWith('email:')) {
    const email = stripPrefix(me.uid, 'email:')
    return { kind: 'email', label: email, copyValue: '' }
  }
  if (provider === 'ton_connect' || me.uid.startsWith('ton_connect:')) {
    const addr = looksLikeTonAddress(me.display_name)
      ? me.display_name
      : looksLikeTonAddress(me.primary_wallet?.address ?? '')
        ? me.primary_wallet!.address
        : me.display_name || stripPrefix(me.uid, 'ton_connect:')
    return { kind: 'ton', label: addr, copyValue: addr }
  }
  if (provider === 'telegram' || me.uid.startsWith('telegram:')) {
    return { kind: 'telegram', label: telegramHandle(me.display_name), copyValue: '' }
  }
  if (provider === 'google' || me.uid.startsWith('google:')) {
    const fromIdentity = (me.identities ?? []).find((row) => row.provider === 'google')?.label
    const label = (fromIdentity?.includes('@') ? fromIdentity : me.display_name.trim()) || 'Google'
    return { kind: 'google', label, copyValue: '' }
  }
  const label = me.display_name.trim() || me.uid
  return { kind: 'other', label, copyValue: '' }
}

function telegramHandle(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return 'Telegram'
  if (trimmed.startsWith('@')) return trimmed
  // Telegram usernames are [a-z][a-z0-9_]{4,31}. A first name like "Faylen"
  // must not become @Faylen. A stored handle without the sigil still gets @.
  if (/^[a-z][a-z0-9_]{4,31}$/.test(trimmed) && /[_0-9]/.test(trimmed)) {
    return `@${trimmed}`
  }
  return trimmed
}

function kindFromProvider(provider: string): ProfileLoginKind {
  switch (provider) {
    case 'email':
      return 'email'
    case 'ton_connect':
      return 'ton'
    case 'telegram':
      return 'telegram'
    case 'google':
      return 'google'
    default:
      return 'other'
  }
}

export function accountAvatarSrc(me: MeSnapshot | null | undefined): string | null {
  if (!me?.has_avatar) return null
  return `/v1/me/avatar?s=${encodeURIComponent(me.session_id)}`
}

export function hasLinkedLogin(methods: LinkedLoginMethod[], kind: ProfileLoginKind): boolean {
  return methods.some((method) => method.kind === kind)
}

export function linkedLoginMethods(me: MeSnapshot): LinkedLoginMethod[] {
  const listed = me.identities ?? []
  if (listed.length > 0) {
    const seen = new Set<ProfileLoginKind>()
    const methods: LinkedLoginMethod[] = []
    for (const row of listed) {
      const kind = kindFromProvider(row.provider)
      if (seen.has(kind)) continue
      seen.add(kind)
      methods.push({ kind, label: row.label || kind })
    }
    return methods
  }
  const identity = profileLoginIdentity(me)
  return [{ kind: identity.kind, label: identity.label }]
}
